import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";

import { Box, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import dayjs from "dayjs";
import { ValueLabel } from "mui-toolkit/ValueLabel";
import { enqueueSnackbar } from "notistack";

import { UsersApiHelper, UsersApiHelperForMember } from "../../api/ApiHelper";
import { AppContext } from "../../App";
import { formatPercentage, formatPrice } from "../../utility/formatters";
import { MASK_NUMERIC, MASK_PERCENTAGE, StatefulControlledTextField } from "../Core/StatefulControlledTextField";
import { TilleyDatePicker } from "../Core/TilleyDatePicker";
import { DEBT_SCHEDULE_PAYMENT_FREQUENCIES } from "../Financial/helpers";
import { FinancialIcon } from "../Icons/Icons";
import { DebtScheduleAmortizationTable } from "./forms/DebtScheduleAmortizationTable";
import { ScenarioAmortizationTable } from "./forms/ScenarioAmortizationTable";
import useDebtScheduleAmortization from "./hooks/useDebtScheduleAmortization";

export const EquipmentRestructuringWorkspace = () => {
  const { debt_schedule_id, equipment_id } = useParams();
  const [debtSchedule, setDebtSchedule] = useState({});
  const [originalAmortization, setOriginalAmortization] = useState([]);
  const [scenario, setScenario] = useState({
    interest_rate: null,
    maturity_date: null,
    payment_frequency: null,
    interest_only_periods: null,
    inception_date: dayjs().format("YYYY-MM-DD"), // Default to today
    additional_principal_amount: 0,
  });

  const { globalState } = useContext(AppContext);
  const api = UsersApiHelperForMember;

  // Get calculated fields from amortization schedule
  const { calculatedFields } = useDebtScheduleAmortization(debt_schedule_id, globalState?.crop_year, globalState);
  useEffect(() => {
    const fetchDebtSchedule = async () => {
      const response = await api.callPost(
        "debtSchedule/Search",
        {
          id: debt_schedule_id,
          producer_token: globalState?.producer_token,
          is_active: true,
        },
        globalState
      );
      setDebtSchedule(response[0]);
    };
    if (globalState?.producer_token && debt_schedule_id) {
      fetchDebtSchedule();
    }
  }, [debt_schedule_id, globalState?.producer_token]);

  // Callback to receive amortization data from DebtScheduleAmortizationTable
  const handleAmortizationDataLoaded = (data) => {
    setOriginalAmortization(data || []);
  };
  const [equipment, setEquipment] = useState({});
  useEffect(() => {
    if (equipment_id) {
      loadEquipmentData();
    }
  }, [equipment_id]);
  const loadEquipmentData = async () => {
    try {
      const equipmentList = await UsersApiHelper.selectEquipmentInventory(
        {
          producer_token: globalState.producer_token,
          year: globalState.crop_year,
          is_active: true,
          id: equipment_id,
        },
        globalState
      );
      const foundEquipment = Array.isArray(equipmentList)
        ? equipmentList.find((eq) => eq.id === equipment_id) || null
        : null;
      setEquipment(foundEquipment);
    } catch (err) {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || "Error loading equipment", {
        preventDuplicate: true,
        variant: "error",
      });
    }
  };

  // Set default scenario values when debtSchedule is loaded
  useEffect(() => {
    if (debtSchedule && Object.keys(debtSchedule).length > 0) {
      setScenario((prev) => ({
        ...prev,
        interest_rate: debtSchedule?.interest_rate,
        maturity_date: debtSchedule?.maturity_date,
        payment_frequency: debtSchedule?.payment_frequency,
        interest_only_periods: debtSchedule?.interest_only_periods,
        balloon_amount: debtSchedule?.balloon_amount,
        // Keep the inception_date and additional_principal_amount from state
      }));
    }
  }, [debtSchedule]);

  const handleScenarioChange = (e) => {
    const { name, value } = e.target;
    setScenario((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setScenario((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <>
      {equipment_id ? (
        <WorkspaceHeader
          title="Restructuring"
          category={{ title: "Financial", icon: <FinancialIcon /> }}
          breadCrumbs={[
            {
              title: "Equipment Inventory",
              path: "/financial/equipment",
            },
            {
              title: equipment?.name || "...",
              path: `/financial/equipment/${equipment_id}`,
            },
            {
              title: debtSchedule?.creditor || "...",
              path: `/financial/equipment/${equipment_id}/${debt_schedule_id}`,
            },
          ]}
        />
      ) : (
        <WorkspaceHeader
          title="Restructuring"
          category={{ title: "Financial", icon: <FinancialIcon /> }}
          breadCrumbs={[
            {
              title: "Debt Schedule",
              path: "/financial/debt_schedule",
            },
            {
              title: debtSchedule?.creditor || "...",
              path: `/financial/debt_schedule/${debt_schedule_id}`,
            },
          ]}
        />
      )}
      <Box sx={{ width: "100%", p: 2 }}>
        <Typography variant="h1">Equipment Loan Amortization</Typography>
        <Grid container spacing={2} sx={{ pt: 2 }}>
          <Grid item xs={12} md={6}>
            <ValueLabel label="Loan Balance:" value={formatPrice(debtSchedule?.amount, 2)} />
            <ValueLabel label="Frequency:" value={debtSchedule?.payment_frequency || "..."} />
            <ValueLabel
              label="Inception Date:"
              value={
                debtSchedule?.inception_date && dayjs(debtSchedule.inception_date).isValid()
                  ? dayjs(debtSchedule.inception_date).format("MM/DD/YYYY")
                  : "..."
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ValueLabel label="Annual Interest Rate:" value={formatPercentage(debtSchedule?.interest_rate, 2)} />
            <ValueLabel label="Scheduled Payment:" value={formatPrice(calculatedFields?.payment_amount || 0, 2)} />
          </Grid>
        </Grid>
      </Box>
      <DebtScheduleAmortizationTable
        debt_schedule_id={debt_schedule_id}
        equipment_id={equipment_id}
        onDataLoaded={handleAmortizationDataLoaded}
      />
      <hr />
      <Box sx={{ p: 2 }}>
        <Typography variant="h1">Scenario Model</Typography>
        <Grid container spacing={2} sx={{ pt: 2 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Interest Rate"
                name="interest_rate"
                defaultValue={scenario.interest_rate || ""}
                onChange={handleScenarioChange}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_PERCENTAGE}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TilleyDatePicker
              name="maturity_date"
              label="Maturity Date"
              value={scenario.maturity_date}
              onChange={handleDateChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="payment-frequency-select-label">Payment Frequency</InputLabel>
              <Select
                labelId="payment-frequency-select-label"
                value={scenario.payment_frequency || ""}
                name="payment_frequency"
                label="Payment Frequency"
                onChange={handleScenarioChange}
              >
                {DEBT_SCHEDULE_PAYMENT_FREQUENCIES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Interest Only Periods"
                name="interest_only_periods"
                defaultValue={scenario.interest_only_periods || ""}
                onChange={handleScenarioChange}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TilleyDatePicker
              name="inception_date"
              label="Inception Date"
              value={scenario.inception_date}
              onChange={handleDateChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Additional Principal Amount"
                name="additional_principal_amount"
                defaultValue={scenario.additional_principal_amount || ""}
                onChange={handleScenarioChange}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <StatefulControlledTextField
                label="Balloon Amount"
                name="balloon_amount"
                defaultValue={scenario.balloon_amount || ""}
                onChange={handleScenarioChange}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <ScenarioAmortizationTable
        debtSchedule={debtSchedule}
        scenario={scenario}
        originalAmortization={originalAmortization}
      />

      <WorkspaceActionBar content={<></>} />
    </>
  );
};
